import React, { useState, useEffect } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './EditFacultyStaffDialog.css';

const DESIGNATION_OPTIONS = [
  { value: 'ডিন', label: 'ডিন' },
  { value: 'রেজিস্ট্রার', label: 'রেজিস্ট্রার' },
  { value: 'ডেপুটি রেজিস্ট্রার', label: 'ডেপুটি রেজিস্ট্রার' },
  { value: 'সেকশন অফিসার', label: 'সেকশন অফিসার' },
  { value: 'এমএলএসএস', label: 'এমএলএসএস' },
  { value: 'ল্যাব অ্যাটেন্ডেন্ট', label: 'ল্যাব অ্যাটেন্ডেন্ট' },
  { value: '__custom__', label: 'কাস্টম (নিজে লিখুন)' },
];

const EditFacultyStaffDialog = ({ isOpen, onClose, onSave, staff, faculty }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    phoneNumber: '',
    designation: '',
    priority: 1
  });
  const [designationOption, setDesignationOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');

  useEffect(() => {
    if (staff) {
      const existingDesignation = staff.designation || '';
      const knownOption = DESIGNATION_OPTIONS.find(
        opt => opt.value !== '__custom__' && opt.value === existingDesignation
      );
      setDesignationOption(knownOption ? existingDesignation : (existingDesignation ? '__custom__' : ''));
      setFormData({
        name: staff.name || '',
        nameEn: staff.nameEn || '',
        email: staff.email || '',
        phoneNumber: staff.phoneNumber || '',
        designation: existingDesignation,
        priority: staff.priority || 1
      });
      setPhotoUrl(staff.dp || staff.photoUrl || '');
    }
  }, [staff]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    setShowCropper(false);
    
    // Create a File object from the blob
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(croppedBlob);
    setPhotoUrl(previewUrl);
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const result = await uploadMedia(selectedFile);
      if (result.success) {
        setPhotoUrl(result.url);
        return result.url;
      } else {
        setError('Upload failed: ' + (result.error || 'Unknown error'));
        return null;
      }
    } catch (error) {
      setError('Upload failed: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoUrl('');
    setSelectedFile(null);
    const fileInput = document.getElementById('edit-faculty-staff-photo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.designation.trim()) {
      setError('Designation is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload photo if a new one was selected and not already uploaded
      let finalPhotoUrl = photoUrl;
      if (selectedFile && !photoUrl.startsWith('http')) {
        finalPhotoUrl = await uploadSelectedFile();
        if (finalPhotoUrl === null) {
          setLoading(false);
          return; // Upload failed, error already set
        }
      }

      const staffData = {
        name: formData.name.trim(),
        nameEn: formData.nameEn?.trim() || '',
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber?.trim() || '',
        designation: formData.designation.trim(),
        faculty: faculty._id,
        faculty_ref: faculty._id,
        departmentId: 1,
        priority: parseInt(formData.priority) || 1,
        dp: finalPhotoUrl || ''
      };

      const response = await api.put(`/faculty-staffs/${staff._id}`, staffData);
      
      if (response.data.success) {
        onSave(response.data.data);
        handleClose();
      } else {
        setError(response.data.message || 'Failed to update faculty staff');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError(err.response?.data?.message || 'Error updating faculty staff. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        nameEn: '',
        email: '',
        phoneNumber: '',
        designation: '',
        priority: 1
      });
      setDesignationOption('');
      setPhotoUrl('');
      setSelectedFile(null);
      setError('');
      onClose();
    }
  };

  if (!isOpen || !staff) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Edit Faculty Staff</h3>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="edit-name">Full Name *</label>
            <input
              type="text"
              id="edit-name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-nameEn">English Name</label>
            <input
              type="text"
              id="edit-nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleInputChange}
              placeholder="Enter English name"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-email">Email *</label>
            <input
              type="email"
              id="edit-email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="edit-phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-designation">Designation *</label>
            <select
              id="edit-designation-select"
              value={designationOption}
              onChange={(e) => {
                const val = e.target.value;
                setDesignationOption(val);
                if (val !== '__custom__') {
                  setFormData(prev => ({ ...prev, designation: val }));
                } else {
                  setFormData(prev => ({ ...prev, designation: '' }));
                }
              }}
              disabled={loading}
              className="form-input"
            >
              <option value="">পদবী নির্বাচন করুন</option>
              {DESIGNATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {designationOption === '__custom__' && (
              <input
                type="text"
                id="edit-designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                placeholder="পদবী লিখুন"
                required
                disabled={loading}
                className="form-input"
                style={{ marginTop: '8px' }}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="edit-priority">Priority</label>
            <input
              type="number"
              id="edit-priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              min="1"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-faculty-staff-photo-upload">Profile Photo</label>
            <div className="avatar-upload-container">
              <div 
                className={`circular-avatar ${selectedFile || photoUrl ? 'has-content' : ''}`}
                onClick={() => !loading && !uploading && document.getElementById('edit-faculty-staff-photo-upload').click()}
              >
                {photoUrl ? (
                  <img src={photoUrl} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="edit-faculty-staff-photo-upload"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading || uploading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              
              {photoUrl && (
                <div className="avatar-actions">
                  <button 
                    type="button" 
                    onClick={handleRemovePhoto}
                    className="remove-button"
                    disabled={loading || uploading}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading || uploading}
            >
              {loading ? 'Updating...' : 'Update Staff'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFile(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EditFacultyStaffDialog;
